import * as Yup from "yup";
import { optionalPhoneSchema } from "../utils/phone";

// Address schema - base
const addressShipmentSchema = Yup.object().shape({
  street: Yup.string().required("validation:address.streetRequired"),
  postalCode: Yup.string().nullable(),
  state: Yup.string().required("validation:address.stateRequired"),
  city: Yup.string().required("validation:address.cityRequired"),
  country: Yup.string().required("validation:address.countryRequired"),
});

// Address schema - optional (for Domestic and minimal mode)
const addressShipmentSchemaOptional = Yup.object().shape({
  street: Yup.string().nullable(),
  postalCode: Yup.string().nullable(),
  state: Yup.string().nullable(),
  city: Yup.string().nullable(),
  country: Yup.string().nullable(),
});

// Carrier address schema
const carrierAddressSchema = Yup.object().shape({
  street: Yup.string().trim().required("validation:address.streetTrimRequired"),
  postalCode: Yup.string().nullable(),
  state: Yup.string().trim().required("validation:address.stateTrimRequired"),
  city: Yup.string().trim().required("validation:address.cityTrimRequired"),
  country: Yup.string()
    .matches(/^[0-9a-fA-F]{24}$/, "validation:address.countryInvalid")
    .required("validation:address.countryRequired"),
});

// Carrier address schema - optional
const carrierAddressSchemaOptional = Yup.object().shape({
  street: Yup.string().trim().nullable(),
  postalCode: Yup.string().nullable(),
  state: Yup.string().trim().nullable(),
  city: Yup.string().trim().nullable(),
  country: Yup.string().nullable(),
});

// Partner info schema - required
const partnerInfoSchema = Yup.object().shape({
  companyName: Yup.string().required("validation:partner.companyNameRequired"),
  registerationCode: Yup.string()
    .transform((value, original) => {
      if (original == null) return "";
      const str = typeof value === "string" ? value : String(value);
      return str.trim();
    })
    .nullable()
    .default("")
    .when("$requireRegistrationCode", {
      is: true,
      then: (schema) => schema.required("validation:partner.registrationCodeRequired"),
      otherwise: (schema) => schema.notRequired(),
    }),
  phoneNumber: optionalPhoneSchema().default(""),
  emailAddress: Yup.string()
    .email("validation:partner.emailInvalid")
    .nullable()
    .transform((value, originalValue) => {
      return originalValue === "" ? null : value;
    }),
  address: addressShipmentSchema,
});

// Partner info schema - optional (for Domestic and minimal mode)
const partnerInfoSchemaOptional = Yup.object().shape({
  companyName: Yup.string().nullable(),
  registerationCode: Yup.string().nullable(),
  phoneNumber: optionalPhoneSchema(),
  emailAddress: Yup.string()
    .email("validation:partner.emailInvalid")
    .nullable(),
  address: addressShipmentSchemaOptional,
});

export const partnerInfoSchemaWithClientCode = partnerInfoSchema.shape({
  clientCode: Yup.string()
    .transform((value, original) => {
      if (original == null) return "";
      const str = typeof value === "string" ? value : String(value);
      return str.trim();
    })
    .nullable()
    .default(""),
  // VAT / tax number — always optional. No `.default()`, so it stays absent
  // from the payload on the Addresses page (whose API rejects unknown keys),
  // exactly like billingEmail below.
  vatNumber: Yup.string()
    .transform((value, original) => (original === "" ? null : value))
    .max(30, "validation:partner.vatNumberTooLong")
    .nullable()
    .notRequired(),
  // Billing block — optional at the profile level (both create and edit).
  // Only shown on the Partners page ($withBilling); format-validated when
  // present. If missing at invoice time, the invoice editor prompts for it.
  paymentTerms: Yup.number()
    .transform((value, original) =>
      original === "" || original == null ? undefined : Number(original),
    )
    .oneOf([7, 14, 30, 60], "validation:partner.paymentTermsInvalid")
    .notRequired(),
  billingEmail: Yup.string()
    .transform((value, original) => (original === "" ? null : value))
    .email("validation:partner.emailInvalid")
    .nullable()
    .notRequired(),
  // Classification flag — a partner usable as a carrier (forwarder). Distinct
  // from the shipment-level forwarder party.
  isForwarder: Yup.boolean().default(false),
});

// Carrier info schema - required
const carrierInfoSchema = Yup.object().shape({
  companyName: Yup.string().trim().required("validation:partner.companyNameRequired"),
  registerationCode: Yup.string()
    .transform((value, original) => {
      if (original == null) return "";
      const str = typeof value === "string" ? value : String(value);
      return str.trim();
    })
    .nullable()
    .default(""),
  phoneNumber: optionalPhoneSchema().default(""),
  emailAddress: Yup.string()
    .email("validation:partner.emailInvalid")
    .nullable()
    .transform((value, originalValue) => {
      return originalValue === "" ? null : value;
    }),
  address: carrierAddressSchema.required(),
  truckLicensePlate: Yup.string()
    .nullable()
    .transform((value) => (value === "" ? null : value))
    .default(null),
  trailerLicensePlate: Yup.string()
    .nullable()
    .transform((value) => (value === "" ? null : value))
    .default(null),
  containerNumber: Yup.string().nullable(),
});

// Carrier info schema - optional (for Domestic and minimal mode)
const carrierInfoSchemaOptional = Yup.object().shape({
  companyName: Yup.string().nullable(),
  registerationCode: Yup.string().nullable(),
  phoneNumber: optionalPhoneSchema(),
  emailAddress: Yup.string().nullable(),
  address: carrierAddressSchemaOptional,
  truckLicensePlate: Yup.string().nullable(),
  trailerLicensePlate: Yup.string().nullable(),
  containerNumber: Yup.string().nullable(),
});

// Cargo item schema - required
const cargoItemSchema = Yup.object().shape({
  quantity: Yup.number()
    .transform((_, originalValue) =>
      originalValue === "" || originalValue === null ? undefined : Number(originalValue),
    )
    .required("validation:cargo.quantityRequired")
    .min(0, "validation:cargo.quantityMin"),
  type: Yup.string()
    .oneOf([
      "EUR",
      "BOX",
      "PLT",
      "PCS",
      "CTN",
      "BAG",
      "DRM",
      "CYL",
      "CRATE",
      "FTL",
      "LTL",
      "OTHER",
    ])
    .required("validation:cargo.packageTypeRequired"),
  weightKg: Yup.number()
    .transform((_, originalValue) =>
      originalValue === "" || originalValue === null ? undefined : Number(originalValue),
    )
    .required("validation:cargo.weightRequired")
    .min(0, "validation:cargo.weightPositive"),
  volumeM3: Yup.number()
    .transform((_, originalValue) => {
      const num = Number(originalValue);
      if (originalValue === "" || originalValue === null || isNaN(num))
        return 0;
      return num;
    })
    .min(0, "validation:cargo.volumePositive")
    .default(0),
  width: Yup.number()
    .transform((_, originalValue) => {
      const num = Number(originalValue);
      if (originalValue === "" || originalValue === null || isNaN(num))
        return 0;
      return num;
    })
    .min(0, "validation:cargo.widthPositive")
    .default(0),
  length: Yup.number()
    .transform((_, originalValue) => {
      const num = Number(originalValue);
      if (originalValue === "" || originalValue === null || isNaN(num))
        return 0;
      return num;
    })
    .min(0, "validation:cargo.lengthPositive")
    .default(0),
  height: Yup.number()
    .transform((_, originalValue) => {
      const num = Number(originalValue);
      if (originalValue === "" || originalValue === null || isNaN(num))
        return 0;
      return num;
    })
    .min(0, "validation:cargo.heightPositive")
    .default(0),
  ldm: Yup.number()
    .transform((_, originalValue) => {
      const num = Number(originalValue);
      if (originalValue === "" || originalValue === null || isNaN(num))
        return 0;
      return num;
    })
    .min(0, "validation:cargo.ldmPositive")
    .default(0),
  chargeableWeight: Yup.number()
    .transform((_, originalValue) => {
      const num = Number(originalValue);
      if (originalValue === "" || originalValue === null || isNaN(num))
        return 0;
      return num;
    })
    .min(0, "validation:cargo.chargeableWeightPositive")
    .default(0),
  description: Yup.string()
    .required("validation:cargo.descriptionRequired")
    .trim()
    .min(1, "validation:cargo.descriptionEmpty"),
  marks: Yup.string().nullable(),
});

// Cargo item schema - optional (for Domestic and minimal mode)
const cargoItemSchemaOptional = Yup.object().shape({
  quantity: Yup.number().nullable(),
  type: Yup.string().nullable(),
  weightKg: Yup.number().nullable(),
  volumeM3: Yup.number().nullable(),
  width: Yup.number().nullable(),
  length: Yup.number().nullable(),
  height: Yup.number().nullable(),
  ldm: Yup.number().nullable(),
  chargeableWeight: Yup.number().nullable(),
  description: Yup.string().nullable(),
  marks: Yup.string().nullable(),
});

const cargoHandlingSchema = Yup.object().shape({
  temperatureSensitive: Yup.boolean().default(false),
  temperatureRange: Yup.string().when("temperatureSensitive", {
    is: true,
    then: (schema) =>
      schema.required(
        "validation:cargo.temperatureRangeRequired",
      ),
    otherwise: (schema) => schema.nullable(),
  }),
  adr: Yup.boolean().default(false),
  imoClass: Yup.string()
    .oneOf([
      "Class1",
      "Class2",
      "Class3",
      "Class4",
      "Class5",
      "Class6",
      "Class7",
      "Class8",
      "Class9",
    ])
    .when("adr", {
      is: true,
      then: (schema) =>
        schema.required("validation:cargo.imoClassRequired"),
      otherwise: (schema) => schema.nullable(),
    }),
  fragile: Yup.boolean().default(false),
  specifications: Yup.string().when("fragile", {
    is: true,
    then: (schema) => schema.required("validation:cargo.specificationsRequired"),
    otherwise: (schema) => schema.nullable(),
  }),
});

export const serviceSnapshotSchema = Yup.object()
  .shape({
    name: Yup.string()
      .trim()
      .required("validation:service.nameRequired")
      .min(1, "validation:service.nameEmpty"),
    description: Yup.string()
      .trim()
      .max(1000, "validation:service.descriptionMax")
      .nullable()
      .optional()
      .transform((value) => (value === "" ? null : value)),
    type: Yup.string()
      .oneOf(
        ["HOUR", "PIECE", "LIFT", "KM", "FLAT", "QTY", "MATRIX"],
        "validation:service.typeInvalid",
      )
      .required("validation:service.typeRequired"),
    price: Yup.number()
      .typeError("validation:service.priceNumber")
      .min(0, "validation:service.priceNegative")
      .required("validation:service.priceRequired"),
    qty: Yup.number()
      .typeError("validation:service.quantityNumber")
      .integer("validation:service.quantityInteger")
      .min(1, "validation:service.quantityMin")
      .required("validation:service.quantityRequired"),
    total: Yup.number()
      .typeError("validation:service.totalNumber")
      .min(0, "validation:service.totalNegative")
      .required("validation:service.totalRequired")
      .test(
        "total-match",
        "validation:service.totalMismatch",
        function (value) {
          const { price, qty } = this.parent;
          if (typeof price === "number" && typeof qty === "number") {
            return Math.abs((value ?? 0) - price * qty) < 0.001;
          }
          return true;
        },
      ),
    addedBy: Yup.string().optional(),
    partnerId: Yup.string().nullable().optional(),
    matrixContext: Yup.object({
      zoneName: Yup.string().nullable().optional(),
      bandLabel: Yup.string().nullable().optional(),
      selectedZoneId: Yup.string().nullable().optional(),
      selectedBandId: Yup.string().nullable().optional(),
    }).optional(),
  })
  .test(
    "matrix-zone-band-required",
    "validation:service.matrixZoneBandRequired",
    function (value) {
      if (value?.type !== "MATRIX") return true; // only applies to MATRIX rows

      const zoneId = value?.matrixContext?.selectedZoneId;
      const bandId = value?.matrixContext?.selectedBandId;

      const zoneOk = typeof zoneId === "string" && zoneId.trim().length > 0;
      const bandOk = typeof bandId === "string" && bandId.trim().length > 0;

      if (!zoneOk && !bandOk) {
        return this.createError({
          message: `validation:service.matrixZoneBandBoth`,
        });
      }
      if (!zoneOk) {
        return this.createError({
          message: `validation:service.matrixZoneRequired`,
        });
      }
      if (!bandOk) {
        return this.createError({
          message: `validation:service.matrixBandRequired`,
        });
      }

      return true;
    },
  )
  .test(
    "matrix-price-resolved",
    "validation:service.matrixPriceResolved",
    function (value) {
      if (value?.type !== "MATRIX") return true;
      const price = Number(value?.price);
      if (!price || price <= 0) {
        return this.createError({
          message: `validation:service.matrixPriceFailed`,
        });
      }
      return true;
    },
  );

// Pickup/Delivery schema - required (always required)
const pickupDeliverySchema = Yup.object().shape({
  date: Yup.string()
    .transform((value, originalValue) => (originalValue === "" ? null : value))
    .required("validation:shipment.dateFieldRequired"),
  time: Yup.string().trim().nullable().optional(),
  ...partnerInfoSchema.fields,
});

// Pickup/Delivery schema - optional (for Domestic pickup and minimal mode)
const pickupDeliverySchemaOptional = Yup.object().shape({
  date: Yup.string().nullable(),
  time: Yup.string().nullable(),
  ...partnerInfoSchemaOptional.fields,
});

// ─── Transport mode (ROAD / SEA / AIR) sub-schemas ───────────────────────────

// Shared forwarder shape — reused in both seaDetails and airDetails.
// Always optional: filled in only when the shipper uses a forwarding agent.
// Sea uses houseBLNumber, air uses hawbNumber — all other fields identical.
const forwarderShape = Yup.object().shape({
  companyName: Yup.string().nullable(),
  contactPerson: Yup.string().nullable(),
  email: Yup.string()
    .email("validation:forwarder.emailInvalid")
    .nullable()
    .transform((value, originalValue) => (originalValue === "" ? null : value)),
  phone: optionalPhoneSchema(),
  reference: Yup.string().nullable(),
  houseBLNumber: Yup.string().nullable(), // sea
  hawbNumber: Yup.string().nullable(), // air
});

// Per-mode detail blocks come in two tiers:
//   • …StrictShape — full required set, used by the document-validation schemas
//     (Validate CMR / Delivery Note).
//   • …SaveShape   — everything optional (format-checked when present), used by
//     the Save schema so creation needs only the universal minimal fields.
// `whenMode` applies a shape only when transportMode matches, stripping the
// block otherwise — one helper for every per-mode block, easy to extend.
const whenMode = (mode: "ROAD" | "SEA" | "AIR", shape: Yup.AnyObjectSchema) =>
  Yup.object().when("transportMode", {
    is: mode,
    then: () => shape,
    otherwise: (s) => s.strip(),
  });

const blTypeField = Yup.mixed<"ORIGINAL" | "SEAWAY" | "TELEX" | "">()
  .transform((value) => (value === null || value === undefined ? "" : value))
  .oneOf(["ORIGINAL", "SEAWAY", "TELEX", ""], "validation:sea.blTypeInvalid")
  .optional();

// No. of original B/Ls — THREE (3) / TWO (2) / ONE (1).
const numberOfOriginalsField = Yup.number()
  .transform((_, originalValue) => {
    if (originalValue === "" || originalValue === null || originalValue === undefined)
      return undefined;
    const num = Number(originalValue);
    return isNaN(num) ? undefined : num;
  })
  .oneOf([1, 2, 3, undefined as any], "validation:sea.numberOfOriginalsInvalid")
  .optional();

// Freight payment terms — Prepaid / Collect (Freight & charges).
const freightPaymentTermsField = Yup.mixed<"Prepaid" | "Collect" | "">()
  .transform((value) => (value === null || value === undefined ? "" : value))
  .oneOf(["Prepaid", "Collect", ""], "validation:sea.freightPaymentTermsInvalid")
  .optional();

// Strict (Validate BOL) variants — required versions of the HBL document fields.
const blTypeStrictField = Yup.mixed<"ORIGINAL" | "SEAWAY" | "TELEX">()
  .transform((value) => (value === null || value === undefined ? "" : value))
  .oneOf(["ORIGINAL", "SEAWAY", "TELEX"], "validation:sea.blTypeRequired")
  .required("validation:sea.blTypeRequired");

const numberOfOriginalsStrictField = Yup.number()
  .transform((_, originalValue) => {
    if (originalValue === "" || originalValue === null || originalValue === undefined)
      return undefined;
    const num = Number(originalValue);
    return isNaN(num) ? undefined : num;
  })
  .oneOf([1, 2, 3], "validation:sea.numberOfOriginalsInvalid")
  .required("validation:sea.numberOfOriginalsRequired");

const freightPaymentTermsStrictField = Yup.mixed<"Prepaid" | "Collect">()
  .transform((value) => (value === null || value === undefined ? "" : value))
  .oneOf(["Prepaid", "Collect"], "validation:sea.freightPaymentTermsRequired")
  .required("validation:sea.freightPaymentTermsRequired");

// Notify Party (HBL) — free strings; email/phone format-checked when present.
const notifyPartyShape = Yup.object()
  .shape({
    company: Yup.string().nullable(),
    contact: Yup.string().nullable(),
    email: Yup.string()
      .email("validation:partner.emailInvalid")
      .nullable()
      .transform((value, originalValue) =>
        originalValue === "" ? null : value,
      ),
    phone: optionalPhoneSchema(),
  })
  .nullable()
  .optional();

const containerTypeField = Yup.mixed<
  | "20DRY"
  | "40DRY"
  | "40HC"
  | "45HC"
  | "20REEFER"
  | "40REEFER"
  | "20OT"
  | "40OT"
  | "20FR"
  | "40FR"
  | "20TANK"
  | "ISOTANK"
  | ""
>()
  .transform((value) => (value === null || value === undefined ? "" : value))
  .oneOf(
    [
      "20DRY",
      "40DRY",
      "40HC",
      "45HC",
      "20REEFER",
      "40REEFER",
      "20OT",
      "40OT",
      "20FR",
      "40FR",
      "20TANK",
      "ISOTANK",
      "",
    ],
    "validation:sea.containerTypeInvalid",
  )
  .optional();

// B/L Remarks — 0, 1, or many optional rows. Each row has a placement
// (location on the B/L) and free text.
const blRemarkLocationField = Yup.mixed<
  | "description_of_goods"
  | "freight_charges"
  | "customs_declaration"
  | "marks_and_numbers"
  | "below_freight_details"
  | "special_instructions"
  | "container_notes"
  | "notify_party"
  | "footer"
  | "body_free"
  | ""
>()
  .transform((value) => (value === null || value === undefined ? "" : value))
  .oneOf(
    [
      "description_of_goods",
      "freight_charges",
      "customs_declaration",
      "marks_and_numbers",
      "below_freight_details",
      "special_instructions",
      "container_notes",
      "notify_party",
      "footer",
      "body_free",
      "",
    ],
    "validation:sea.blRemarkLocationInvalid",
  )
  .optional();

const blRemarksField = Yup.array()
  .of(
    Yup.object().shape({
      location: blRemarkLocationField,
      text: Yup.string().nullable(),
    }),
  )
  .nullable()
  .optional();

// ── AWB (Air Waybill) document block ─────────────────────────────────────────
// freightPaymentTerms/otherChargesPayment map to the IATA CHGS code (PP/CC).
const paymentTermsStrict = (msg: string) =>
  Yup.mixed<"Prepaid" | "Collect">()
    .transform((value) => (value === null || value === undefined ? "" : value))
    .oneOf(["Prepaid", "Collect"], msg)
    .required(msg);

const paymentTermsSave = Yup.mixed<"Prepaid" | "Collect" | "">()
  .transform((value) => (value === null || value === undefined ? "" : value))
  .oneOf(["Prepaid", "Collect", ""], "validation:awb.paymentTermsInvalid")
  .optional();

// specialHandlingCodes / remarks are free arrays of strings (0, 1, or many).
const stringArrayField = Yup.array()
  .of(Yup.string().nullable())
  .nullable()
  .optional();

// Notify party — all four fields optional, printed stacked in one AWB cell.
const awbNotifyPartyShape = Yup.object().shape({
  companyName: Yup.string().nullable(),
  contactPerson: Yup.string().nullable(),
  email: Yup.string()
    .email("validation:awb.emailInvalid")
    .nullable()
    .transform((value, originalValue) => (originalValue === "" ? null : value)),
  phone: optionalPhoneSchema(),
});

const awbStrictShape = Yup.object().shape({
  awbNumber: Yup.string().trim().required("validation:awb.awbNumberRequired"),
  awbDate: Yup.string()
    .transform((value, originalValue) => (originalValue === "" ? null : value))
    .required("validation:awb.awbDateRequired"),
  issuingAgentName: Yup.string()
    .trim()
    .required("validation:awb.issuingAgentNameRequired"),
  issuingAgentCity: Yup.string()
    .trim()
    .required("validation:awb.issuingAgentCityRequired"),
  freightPaymentTerms: paymentTermsStrict(
    "validation:awb.freightPaymentTermsRequired",
  ),
  otherChargesPayment: paymentTermsStrict(
    "validation:awb.otherChargesPaymentRequired",
  ),
  declaredValueCarriage: Yup.string().nullable(),
  declaredValueCustoms: Yup.string().nullable(),
  currency: Yup.string().trim().required("validation:awb.currencyRequired"),
  specialHandlingCodes: stringArrayField,
  remarks: stringArrayField,
  notifyParty: awbNotifyPartyShape,
});

const awbSaveShape = Yup.object().shape({
  awbNumber: Yup.string().nullable(),
  awbDate: Yup.string().nullable(),
  issuingAgentName: Yup.string().nullable(),
  issuingAgentCity: Yup.string().nullable(),
  freightPaymentTerms: paymentTermsSave,
  otherChargesPayment: paymentTermsSave,
  declaredValueCarriage: Yup.string().nullable(),
  declaredValueCustoms: Yup.string().nullable(),
  currency: Yup.string().nullable(),
  specialHandlingCodes: stringArrayField,
  remarks: stringArrayField,
  notifyParty: awbNotifyPartyShape,
});

// ── Strict (document-validation) shapes ──────────────────────────────────────
export const seaDetailsStrictShape = Yup.object().shape({
  shippingLine: Yup.string()
    .trim()
    .required("validation:sea.shippingLineRequired"),
  vesselName: Yup.string().trim().required("validation:sea.vesselNameRequired"),
  imoNumber: Yup.string().nullable(),
  voyageNumber: Yup.string().nullable(),
  bookingNumber: Yup.string().nullable(),
  portOfLoading: Yup.string()
    .trim()
    .required("validation:sea.portOfLoadingRequired"),
  portOfDischarge: Yup.string()
    .trim()
    .required("validation:sea.portOfDischargeRequired"),
  placeOfReceipt: Yup.string().nullable(),
  placeOfDelivery: Yup.string().nullable(),
  notifyParty: notifyPartyShape,
  containerType: containerTypeField,
  sealNumber: Yup.string().nullable(),
  masterBLNumber: Yup.string()
    .trim()
    .required("validation:sea.masterBLNumberRequired"),
  blNumber: Yup.string().trim().required("validation:sea.blNumberRequired"),
  blDate: Yup.string()
    .transform((value, originalValue) => (originalValue === "" ? null : value))
    .required("validation:sea.blDateRequired"),
  blType: blTypeStrictField,
  numberOfOriginals: numberOfOriginalsStrictField,
  freightPaymentTerms: freightPaymentTermsStrictField,
  etd: Yup.string()
    .transform((value, originalValue) => (originalValue === "" ? null : value))
    .required("validation:sea.etdRequired"),
  eta: Yup.string()
    .transform((value, originalValue) => (originalValue === "" ? null : value))
    .required("validation:sea.etaRequired"),
  blRemarks: blRemarksField,
  forwarder: forwarderShape,
});

export const airDetailsStrictShape = Yup.object().shape({
  airlineName: Yup.string().trim().required("validation:air.airlineNameRequired"),
  flightNumber: Yup.string()
    .trim()
    .required("validation:air.flightNumberRequired"),
  airportOfDeparture: Yup.string()
    .trim()
    .required("validation:air.airportOfDepartureRequired"),
  airportOfArrival: Yup.string()
    .trim()
    .required("validation:air.airportOfArrivalRequired"),
  mawbNumber: Yup.string().trim().required("validation:air.mawbNumberRequired"),
  mawbDate: Yup.string().nullable(),
  uldNumber: Yup.string().nullable(),
  etd: Yup.string()
    .transform((value, originalValue) => (originalValue === "" ? null : value))
    .required("validation:air.etdRequired"),
  eta: Yup.string()
    .transform((value, originalValue) => (originalValue === "" ? null : value))
    .required("validation:air.etaRequired"),
  forwarder: forwarderShape,
  awb: awbStrictShape.required("validation:awb.required"),
});

// ── Save shapes (everything optional, format-checked when present) ────────────
const seaDetailsSaveShape = Yup.object().shape({
  shippingLine: Yup.string().nullable(),
  vesselName: Yup.string().nullable(),
  imoNumber: Yup.string().nullable(),
  voyageNumber: Yup.string().nullable(),
  bookingNumber: Yup.string().nullable(),
  portOfLoading: Yup.string().nullable(),
  portOfDischarge: Yup.string().nullable(),
  placeOfReceipt: Yup.string().nullable(),
  placeOfDelivery: Yup.string().nullable(),
  notifyParty: notifyPartyShape,
  containerType: containerTypeField,
  sealNumber: Yup.string().nullable(),
  masterBLNumber: Yup.string().nullable(),
  blNumber: Yup.string().nullable(),
  blDate: Yup.string().nullable(),
  blType: blTypeField,
  numberOfOriginals: numberOfOriginalsField,
  freightPaymentTerms: freightPaymentTermsField,
  etd: Yup.string().nullable(),
  eta: Yup.string().nullable(),
  blRemarks: blRemarksField,
  forwarder: forwarderShape,
});

const airDetailsSaveShape = Yup.object().shape({
  airlineName: Yup.string().nullable(),
  flightNumber: Yup.string().nullable(),
  airportOfDeparture: Yup.string().nullable(),
  airportOfArrival: Yup.string().nullable(),
  mawbNumber: Yup.string().nullable(),
  mawbDate: Yup.string().nullable(),
  uldNumber: Yup.string().nullable(),
  etd: Yup.string().nullable(),
  eta: Yup.string().nullable(),
  forwarder: forwarderShape,
  awb: awbSaveShape,
});

// ─── Shared optional/common fields ───────────────────────────────────────────

const sharedOptionalFields = {
  transportMode: Yup.mixed<"ROAD" | "SEA" | "AIR">()
    .transform((value) =>
      value === null || value === undefined || value === "" ? "ROAD" : value,
    )
    .oneOf(["ROAD", "SEA", "AIR"], "validation:shipment.transportModeInvalid")
    .default("ROAD"),

  // Save-mode: sea/air blocks are optional (format-checked when present) and
  // stripped for every non-matching mode so stale values never reach the
  // backend. Full required-field enforcement happens in the document-validation
  // schemas (shipmentSchemaInternational / shipmentSchemaDomestic), which
  // override these with the strict shapes.
  seaDetails: whenMode("SEA", seaDetailsSaveShape),
  airDetails: whenMode("AIR", airDetailsSaveShape),

  // Top-level forwarder — used by ROAD (SEA/AIR keep their nested forwarder
  // under sea/airDetails). Always optional, format-checked when present.
  forwarder: forwarderShape,

  status: Yup.string()
    .oneOf([
      "Order",
      "ReadyForPickup",
      "AwaitingPickup",
      "InTransit",
      "Completed",
    ])
    .default("Order"),

  referenceNumber: Yup.string()
    .transform((value, original) => {
      if (original == null) return "";
      const str = typeof value === "string" ? value : String(value);
      return str.trim();
    })
    .nullable()
    .default(""),

  assignedDriver: Yup.string()
    .nullable()
    .transform((value) => (value === "" ? null : value))
    .default(null),

  cargoHandling: cargoHandlingSchema,

  services: Yup.array()
    .of(serviceSnapshotSchema)
    .required("validation:service.servicesRequired")
    .test(
      "single-client-services",
      "validation:service.singleClient",
      function (services) {
        if (!services || services.length === 0) return true;

        const clientPartnerIds = (services as any[])
          .map((s) => s?.partnerId)
          .filter(
            (id): id is string =>
              typeof id === "string" && id.trim().length > 0,
          );

        const unique = new Set(clientPartnerIds);

        if (unique.size <= 1) return true;

        return this.createError({
          message: "validation:service.multipleClients",
        });
      },
    ),

  // Internal cost services — optional, never invoiced. Reuses the service
  // snapshot shape (price/qty/matrix) but carries no invoicing requirement.
  costServices: Yup.array()
    .of(serviceSnapshotSchema)
    .optional()
    .default([]),

  incoterms: Yup.mixed<
    | "EXW"
    | "FCA"
    | "CPT"
    | "CIP"
    | "DAP"
    | "DPU"
    | "DDP"
    | "FAS"
    | "FOB"
    | "CFR"
    | "CIF"
    | ""
  >()
    .transform((value) => (value === null || value === undefined ? "" : value))
    .oneOf(
      [
        "EXW",
        "FCA",
        "CPT",
        "CIP",
        "DAP",
        "DPU",
        "DDP",
        "FAS",
        "FOB",
        "CFR",
        "CIF",
        "",
      ],
      "validation:shipment.incotermInvalid",
    )
    .optional(),

  loadType: Yup.mixed<"Full Load" | "Partial Load" | "">()
    .transform((value) => (value === null || value === undefined ? "" : value))
    .oneOf(["Full Load", "Partial Load", ""], "validation:shipment.loadTypeInvalid")
    .optional(),

  place: Yup.string()
    .trim()
    .transform((value) => (value === null || value === undefined ? "" : value))
    .optional(),

  direction: Yup.string()
    .oneOf(["Inbound", "Outbound", ""], "validation:shipment.directionInvalid")
    .transform((value) => (value === null || value === undefined ? "" : value))
    .optional(),

  additionalNotes: Yup.string().nullable(),

  attachments: Yup.array()
    .of(
      Yup.mixed<File>().test("file-size", "validation:shipment.fileTooLarge", (file) => {
        return file ? file.size <= 10 * 1024 * 1024 : true;
      }),
    )
    .max(10, "validation:shipment.maxFiles")
    .optional(),

  driverAttachments: Yup.array()
    .of(
      Yup.mixed<File>().test("file-size", "validation:shipment.fileTooLarge", (file) => {
        return file ? file.size <= 10 * 1024 * 1024 : true;
      }),
    )
    .max(10, "validation:shipment.maxFiles")
    .optional(),

  pricing: Yup.object()
    .shape({
      totalBuyingPrice: Yup.number()
        .min(0, "validation:shipment.priceMin")
        .transform((_, originalValue) => {
          const num = Number(originalValue);
          if (originalValue === "" || isNaN(num)) return 0;
          return num;
        })
        .nullable(),
      totalSellingPrice: Yup.number()
        .min(0, "validation:shipment.priceMin")
        .transform((_, originalValue) => {
          const num = Number(originalValue);
          if (originalValue === "" || isNaN(num)) return 0;
          return num;
        })
        .nullable(),
      pricePerKm: Yup.number()
        .min(0, "validation:shipment.priceMin")
        .transform((_, originalValue) => {
          const num = Number(originalValue);
          if (originalValue === "" || isNaN(num)) return 0;
          return num;
        })
        .nullable(),
      mileage: Yup.number()
        .min(0, "validation:shipment.priceMin")
        .transform((_, originalValue) => {
          const num = Number(originalValue);
          if (originalValue === "" || isNaN(num)) return 0;
          return num;
        })
        .nullable(),
      currency: Yup.string()
        .oneOf(["USD", "EUR", "GBP", "PKR", "AED"], "validation:shipment.currencyInvalid")
        .default("EUR")
        .nullable(),
    })
    .optional(),

  clientSync: Yup.object()
    .shape({
      clientSameAsPickup: Yup.boolean().default(false),
      clientSameAsDelivery: Yup.boolean().default(false),
    })
    .default({
      clientSameAsPickup: false,
      clientSameAsDelivery: false,
    }),
};

// ─── Save-mode helpers (format checks only; nothing required) ───────────────

// Phone format in Save mode is the same rule the strict schemas use — one
// definition, in `utils/phone`. Optional here as everywhere.
const phoneOptionalWithFormat = optionalPhoneSchema();

const emailOptionalWithFormat = Yup.string()
  .email("validation:partner.emailInvalid")
  .nullable()
  .transform((value, originalValue) => (originalValue === "" ? null : value));

// Numeric helper: optional, but if filled must be a non-negative number.
const nonNegativeOptional = (msg: string) =>
  Yup.number()
    .transform((_, originalValue) => {
      if (originalValue === "" || originalValue === null || originalValue === undefined) {
        return undefined;
      }
      const num = Number(originalValue);
      return isNaN(num) ? undefined : num;
    })
    .nullable()
    .min(0, msg);

// Address used in Save mode for pickup/delivery: only city is required.
// City is more specific than country (every shipment in Estonia would share
// country=EE), so it captures the meaningful "where" of an order at draft time.
const addressShipmentSchemaSave = Yup.object().shape({
  street: Yup.string().nullable(),
  postalCode: Yup.string().nullable(),
  state: Yup.string().nullable(),
  city: Yup.string().required("validation:address.cityRequired"),
  country: Yup.string().nullable(),
});

// Pickup/Delivery in Save mode: country required, everything else format-checked.
const pickupDeliverySchemaSave = Yup.object().shape({
  date: Yup.string().nullable(),
  time: Yup.string().trim().nullable(),
  companyName: Yup.string().nullable(),
  registerationCode: Yup.string().nullable(),
  phoneNumber: phoneOptionalWithFormat,
  emailAddress: emailOptionalWithFormat,
  address: addressShipmentSchemaSave,
});

// Client in Save mode: company name required; address fully optional.
const clientSchemaSave = Yup.object().shape({
  companyName: Yup.string()
    .trim()
    .required("validation:partner.companyNameRequired"),
  registerationCode: Yup.string().nullable(),
  phoneNumber: phoneOptionalWithFormat,
  emailAddress: emailOptionalWithFormat,
  address: addressShipmentSchemaOptional,
});

// Carrier in Save mode: nothing required; format-only checks.
const carrierSchemaSave = Yup.object().shape({
  companyName: Yup.string().trim().nullable(),
  registerationCode: Yup.string().nullable(),
  phoneNumber: phoneOptionalWithFormat,
  emailAddress: emailOptionalWithFormat,
  address: carrierAddressSchemaOptional,
  truckLicensePlate: Yup.string().nullable(),
  trailerLicensePlate: Yup.string().nullable(),
  containerNumber: Yup.string().nullable(),
});

// Cargo item in Save mode: nothing required, numerics must be non-negative.
const cargoItemSchemaSave = Yup.object().shape({
  quantity: nonNegativeOptional("validation:cargo.quantityMin"),
  type: Yup.string().nullable(),
  weightKg: nonNegativeOptional("validation:cargo.weightPositive"),
  volumeM3: nonNegativeOptional("validation:cargo.volumePositive"),
  width: nonNegativeOptional("validation:cargo.widthPositive"),
  length: nonNegativeOptional("validation:cargo.lengthPositive"),
  height: nonNegativeOptional("validation:cargo.heightPositive"),
  ldm: nonNegativeOptional("validation:cargo.ldmPositive"),
  chargeableWeight: nonNegativeOptional(
    "validation:cargo.chargeableWeightPositive",
  ),
  description: Yup.string().nullable(),
  marks: Yup.string().nullable(),
});

// ==================== INTERNATIONAL SHIPMENT SCHEMA ====================
// CMR validation (International) — used by the "Validate CMR" button.

export const shipmentSchemaInternational = Yup.object().shape({
  type: Yup.string()
    .oneOf(["International", "Domestic"])
    .default("International")
    .required("validation:shipment.typeRequired"),

  documentRequire: Yup.boolean().default(true),

  shipmentDate: Yup.string().required("validation:shipment.dateRequired"),

  pickup: pickupDeliverySchema.required("validation:shipment.pickupRequired"),

  delivery: pickupDeliverySchema.required("validation:shipment.deliveryRequired"),

  carrier: carrierInfoSchema.required("validation:shipment.carrierRequired"),

  client: partnerInfoSchema.required("validation:shipment.clientRequired"),

  cargoItems: Yup.array()
    .of(cargoItemSchema)
    .min(1, "validation:cargo.atLeastOneItem")
    .required("validation:cargo.cargoItemsRequired"),

  ...sharedOptionalFields,

  // Document validation (Validate CMR): override the lax save blocks with the
  // strict full-field sea/air shapes. Runs the complete mode-appropriate check.
  seaDetails: whenMode("SEA", seaDetailsStrictShape),
  airDetails: whenMode("AIR", airDetailsStrictShape),
});

// ==================== DOMESTIC SHIPMENT SCHEMA ====================
// CMR validation (Domestic) — used by the "Validate CMR" button.

export const shipmentSchemaDomestic = Yup.object().shape({
  type: Yup.string()
    .oneOf(["International", "Domestic"])
    .default("Domestic")
    .required("validation:shipment.typeRequired"),

  documentRequire: Yup.boolean().default(true),

  shipmentDate: Yup.string().required("validation:shipment.dateRequired"),

  pickup: pickupDeliverySchemaOptional,

  delivery: pickupDeliverySchema.required("validation:shipment.deliveryRequired"),

  carrier: carrierInfoSchemaOptional,

  client: partnerInfoSchemaOptional,

  cargoItems: Yup.array().of(cargoItemSchemaOptional).optional(),

  ...sharedOptionalFields,

  // Document validation (Validate Delivery Note): override the lax save blocks
  // with the strict full-field sea/air shapes.
  seaDetails: whenMode("SEA", seaDetailsStrictShape),
  airDetails: whenMode("AIR", airDetailsStrictShape),
});

// ==================== SEA SHIPMENT SCHEMA ====================
// "Validate BOL" + the BOL preview gate. For sea, pickup = Shipper/Consignor
// and delivery = Consignee. A road carrier is NOT required — the vessel / B/L
// data lives in the strict seaDetails block instead. transportMode is always
// SEA when this schema runs, so whenMode applies the strict sea shape.

export const shipmentSchemaSea = Yup.object().shape({
  type: Yup.string()
    .oneOf(["International", "Domestic"])
    .default("International")
    .required("validation:shipment.typeRequired"),

  documentRequire: Yup.boolean().default(true),

  shipmentDate: Yup.string().required("validation:shipment.dateRequired"),

  pickup: pickupDeliverySchema.required("validation:shipment.pickupRequired"),

  delivery: pickupDeliverySchema.required("validation:shipment.deliveryRequired"),

  carrier: carrierInfoSchemaOptional,

  client: partnerInfoSchema.required("validation:shipment.clientRequired"),

  cargoItems: Yup.array()
    .of(cargoItemSchema)
    .min(1, "validation:cargo.atLeastOneItem")
    .required("validation:cargo.cargoItemsRequired"),

  ...sharedOptionalFields,

  // The sea block is mandatory and fully checked here.
  seaDetails: whenMode("SEA", seaDetailsStrictShape),
  airDetails: whenMode("AIR", airDetailsStrictShape),
});

// ==================== AIR SHIPMENT SCHEMA ====================
// "Validate AWB" + the AWB preview gate. Shipper = pickup, Consignee = delivery.
// A road carrier is NOT required — the flight / AWB data lives in the strict
// airDetails block. transportMode is always AIR when this schema runs.

export const shipmentSchemaAir = Yup.object().shape({
  type: Yup.string()
    .oneOf(["International", "Domestic"])
    .default("International")
    .required("validation:shipment.typeRequired"),

  documentRequire: Yup.boolean().default(true),

  shipmentDate: Yup.string().required("validation:shipment.dateRequired"),

  pickup: pickupDeliverySchema.required("validation:shipment.pickupRequired"),

  delivery: pickupDeliverySchema.required("validation:shipment.deliveryRequired"),

  carrier: carrierInfoSchemaOptional,

  client: partnerInfoSchema.required("validation:shipment.clientRequired"),

  cargoItems: Yup.array()
    .of(cargoItemSchema)
    .min(1, "validation:cargo.atLeastOneItem")
    .required("validation:cargo.cargoItemsRequired"),

  ...sharedOptionalFields,

  // The air block is mandatory and fully checked here.
  seaDetails: whenMode("SEA", seaDetailsStrictShape),
  airDetails: whenMode("AIR", airDetailsStrictShape),
});

// ==================== SAVE SHIPMENT SCHEMA ====================
// Used for every Save (Create/Update). Light by design:
//   - type, shipmentDate, client.companyName,
//     pickup.address.country, delivery.address.country  → required.
//   - everything else optional, but format-checked when present.
// The stricter International/Domestic schemas above are only used by the
// "Validate CMR" action in the form footer.

export const shipmentSchemaSave = Yup.object().shape({
  type: Yup.string()
    .oneOf(["International", "Domestic"])
    .required("validation:shipment.typeRequired"),

  // Kept for backwards-compat with existing records and the eFTI gate.
  // Not surfaced as a checkbox anymore; defaults to true on new saves.
  documentRequire: Yup.boolean().default(true),

  shipmentDate: Yup.string().required("validation:shipment.dateRequired"),

  pickup: pickupDeliverySchemaSave.required(),

  delivery: pickupDeliverySchemaSave.required(),

  carrier: carrierSchemaSave.nullable().optional(),

  client: clientSchemaSave.required("validation:shipment.clientRequired"),

  cargoItems: Yup.array().of(cargoItemSchemaSave).optional(),

  ...sharedOptionalFields,
});

// ==================== MAIN SCHEMA ====================
// Form resolver: always the Save schema, regardless of type.
// "Validate CMR" runs shipmentSchemaInternational/Domestic on demand.

export const shipmentSchema = shipmentSchemaSave;

export const createShipmentSchema = shipmentSchema;
export const updateShipmentSchema = shipmentSchema;
